import type { Currency } from 'libs/shared-lib/interfaces';
import type { CurrencyNumberConfig } from '../provider/currency.provider';

export const fractionSplitter = "'"; // eslint-disable-line
export const fractionSplitterDefault = '’';
export const numberSuffixTemplate = '.–';

export function getNumberFormatLocale(locale: string, config: CurrencyNumberConfig): `${string}-${Uppercase<string>}` {
    if (config.localeReplacement && config.localeReplacement[locale]) {
        locale = config.localeReplacement[locale];
    }
    return `${locale}-${config.country.toLocaleUpperCase() as Uppercase<string>}`;
}

export function getNumberFormatWithCurrency(
    numberFormatLocale: string,
    digits: number,
    currency: Currency,
): Intl.NumberFormat {
    return new Intl.NumberFormat(numberFormatLocale, {
        style: 'currency',
        currency,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}

export function getNumberFormat(numberFormatLocale: string, digits: number): Intl.NumberFormat {
    return new Intl.NumberFormat(numberFormatLocale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}

export function getCurrencyDefaultValue(
    value: string,
    lang: string,
    config: CurrencyNumberConfig,
    digits: number,
    numberSuffix?: string,
): string {
    const numberSuffixTemp = numberSuffix ?? config.globalSufixTemplate ?? numberSuffixTemplate;
    const suffix = digits === 0 ? numberSuffixTemp : '';
    let result = '';

    if (config.addCurrency) {
        if (configHasNumberSuffixAfterValueAndCurrency(config, lang)) {
            result = `${config.currency} ${value}${suffix}`;
        } else {
            result = `${value}${suffix} ${config.currency}`;
        }
    } else {
        result = `${value}${suffix}`;
    }
    result = formatNumberFixed(result, lang, digits);

    if (configHasSplitterReplacement(config, lang)) {
        return result.replace(fractionSplitterDefault, fractionSplitter);
    }

    return result;
}

export function buildValueFromParts(
    valueParts: Intl.NumberFormatPart[],
    lang: string,
    config: CurrencyNumberConfig,
    digits: number,
    numberSuffix?: string,
): string {
    const numberSuffixTemp = numberSuffix ? numberSuffix : numberSuffixTemplate;
    const suffix = digits === 0 ? numberSuffixTemp : '';

    let currencyString = '';
    let valueString = '';
    let result = '';

    for (const part of valueParts) {
        if (part.type === 'currency') {
            currencyString = `<span class="price-currency">${part.value}</span>`;
        } else if (part.type !== 'literal') {
            valueString = `${valueString + part.value}`;
        }
    }

    if (configHasNumberSuffixAfterValueAndCurrency(config, lang)) {
        result = `${currencyString} <span class="price-value">${valueString.trim() + suffix}</span>`;
    } else {
        result = `<span class="price-value">${valueString.trim()}</span> ${currencyString}`;
    }

    result = formatNumberFixed(result, lang, digits);

    if (configHasSplitterReplacement(config, lang)) {
        return result.replace(fractionSplitterDefault, fractionSplitter);
    }

    return result;
}

export function getHTMLStringNumber(valueString: string, lang: string, digits: number, config: CurrencyNumberConfig) {
    const value = formatNumberFixed(valueString, lang, digits);
    return `<span class="price-value">${value}${config.numberSuffixAfterValueAndCurrency ? numberSuffixTemplate : ''}</span>`;
}

export function getWholesaleNumber(valueString: string, lang: string, digits: number, numberSuffix = false) {
    const value = formatNumberFixed(valueString, lang, digits);
    return `${value}${numberSuffix ? numberSuffixTemplate : ''}`;
}

function configHasNumberSuffixAfterValueAndCurrency(config: CurrencyNumberConfig, lang: string): boolean {
    return config.numberSuffixAfterValueAndCurrency?.includes(lang) || false;
}

export function configHasSplitterReplacement(config: CurrencyNumberConfig, lang: string): boolean {
    return config.fractionSplitterReplacementLocales?.includes(lang) || false;
}

// Note: Intl.NumberFormat('it-CH') may produce different results on different platforms.
// For example, on iOS/Safari, the decimal separator might be a comma (",") instead of a dot ("."),
// even though 'it-CH' (Italian - Switzerland) typically uses a dot as the decimal separator.
// This is due to differences in how each operating system (especially Apple devices) implements locale data.
// If consistent formatting is required across all platforms, consider using a fixed locale like 'en-US',
// or implement custom formatting logic as a fallback.
function formatNumberFixed(value: string, lang: string, digits: number): string {
    if ((lang === 'fr' || lang === 'it') && digits !== 0) {
        return value.replace(',', '.');
    }
    return value;
}
